import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronUp, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { BrandButton } from "@/components/kit/BrandButton";
import { EmptyState } from "@/components/kit/EmptyState";
import { PageBand } from "@/components/kit/PageBand";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/ppcp";
import { OPERATOR, PAGES } from "@/constants/testIds";

const when = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "Never";

const Stat = ({ label, value, testId, tone = "teal" }) => (
  <div className="rounded-2xl bg-brand-teal/[0.04] px-4 py-3">
    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-teal/55">{label}</p>
    <p
      data-testid={testId}
      className={`mt-1 font-display text-2xl font-semibold ${
        tone === "alert" && Number(value) > 0 ? "text-brand-orangeInk" : "text-brand-teal"
      }`}
    >
      {value}
    </p>
  </div>
);

const PlanCard = ({ card, onSync, busy }) => {
  const run = card.last_run ?? {};
  const slug = card.program_slug;
  return (
    <section data-testid={OPERATOR.card(slug)} className="ps-card ps-card-pad space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="ps-eyebrow">Meal plan</p>
          <h2 className="mt-1.5 font-display text-2xl font-semibold text-brand-teal">{slug}</h2>
          <p data-testid={OPERATOR.lastSync(slug)} className="mt-1 text-sm text-brand-teal/70">
            Last sync: {when(card.last_sync_time)} · {card.synced} of {card.mapped} mapped recipes cached
            {card.pending_mapping?.length
              ? ` · ${card.pending_mapping.length} still awaiting a WordPress recipe ID`
              : ""}
          </p>
        </div>
        <BrandButton
          name={`sync-${slug}`}
          data-testid={OPERATOR.syncNow(slug)}
          onClick={() => onSync({ slug })}
          disabled={busy}
        >
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Sync Now
        </BrandButton>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Updated" value={run.recipes_updated ?? 0} testId={OPERATOR.updated(slug)} />
        <Stat label="Skipped" value={run.recipes_skipped ?? 0} testId={OPERATOR.skipped(slug)} />
        <Stat label="Failed" value={run.recipes_failed ?? 0} testId={OPERATOR.failed(slug)} tone="alert" />
        <Stat
          label="Pending mapping"
          value={card.pending_mapping?.length ?? 0}
          testId={OPERATOR.pending(slug)}
        />
      </div>

      <div data-testid={OPERATOR.validation(slug)} className="text-sm">
        {run.validation_errors?.length ? (
          <ul className="space-y-1.5 rounded-2xl bg-brand-orange/[0.07] p-4 text-brand-orangeInk">
            {run.validation_errors.map((v) => (
              <li key={v.recipe_id}>
                Recipe {v.recipe_id}: {v.problems}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-brand-teal/60">No validation errors in the last run.</p>
        )}
      </div>

      {(card.orphaned?.length > 0 || card.quarantined?.length > 0) && (
        <p data-testid={OPERATOR.flags(slug)} className="text-sm text-brand-orangeInk">
          {card.orphaned?.length ? `${card.orphaned.length} orphaned` : ""}
          {card.orphaned?.length && card.quarantined?.length ? " · " : ""}
          {card.quarantined?.length ? `${card.quarantined.length} quarantined` : ""} — the last good copy
          is still being served to readers.
        </p>
      )}
    </section>
  );
};

export default function OperatorConsole() {
  const { canPreview, status } = useAuth();
  const [overview, setOverview] = useState(null);
  const [runs, setRuns] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/recipes/sync");
    setOverview(data);
  }, []);

  useEffect(() => {
    if (canPreview) load().catch(() => setOverview(false));
  }, [canPreview, load]);

  const loadRuns = useCallback(async () => {
    const { data } = await api.get("/admin/recipes/sync/runs");
    setRuns(data.runs ?? []);
  }, []);

  const sync = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/recipes/sync", body);
      toast.success(
        `${body.slug ?? "All plans"}: ${data.recipes_updated} updated, ${data.recipes_skipped} skipped, ${data.recipes_failed} failed`
      );
      await load();
      if (showHistory) await loadRuns();
    } catch {
      toast.error("Sync could not start. Readers are unaffected — the cache keeps serving.");
    } finally {
      setBusy(false);
    }
  };

  if (status === "loading") {
    return (
      <AppShell title="Operator Console" nav={false} size="wide">
        <div className="ps-card h-56 animate-pulse" />
      </AppShell>
    );
  }

  if (!canPreview) {
    return (
      <AppShell title="Operator Console" nav={false} size="reading">
        <div data-testid={OPERATOR.denied} className="contents">
          <EmptyState
            icon={ShieldAlert}
            title="Operators only"
            description="This console is available to Plattershare content operators. Your account does not carry that capability."
          />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Operator Console" nav={false} size="wide">
      <div data-testid={PAGES.operator} className="contents">
        <PageBand
          eyebrow="Recipe Sync"
          title="Operator Console"
          accent="Console"
          intro="WordPress owns the recipes; this is where you push them into the app. Reading never waits for WordPress — a sync only refreshes the copy readers are already being served."
        />

        <section className="ps-card ps-card-pad flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-display text-xl font-semibold text-brand-teal">Every meal plan</h2>
            <p className="mt-1 text-sm text-brand-teal/70">
              Sync all plans at once, or use Sync Now on a single plan below.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <BrandButton name="full-sync" data-testid={OPERATOR.fullSync} onClick={() => sync({})} disabled={busy}>
              <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Full Sync
            </BrandButton>
            <BrandButton
              name="sync-history"
              variant="outline"
              data-testid={OPERATOR.historyToggle}
              onClick={() => {
                setShowHistory((v) => !v);
                if (!runs) loadRuns().catch(() => setRuns([]));
              }}
            >
              {showHistory ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              View Sync History
            </BrandButton>
          </div>
        </section>

        {overview === false && (
          <section className="ps-card ps-card-pad text-sm text-brand-orangeInk">
            The sync dashboard could not be loaded.
          </section>
        )}

        {overview?.programs?.map((card) => (
          <PlanCard key={card.program_slug} card={card} onSync={sync} busy={busy} />
        ))}

        {showHistory && (
          <section data-testid={OPERATOR.history} className="ps-card ps-card-pad">
            <h2 className="font-display text-xl font-semibold text-brand-teal">Sync history</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-bold uppercase tracking-[0.14em] text-brand-teal/55">
                    <th className="pb-3 pr-4">Finished</th>
                    <th className="pb-3 pr-4">Plan</th>
                    <th className="pb-3 pr-4">Trigger</th>
                    <th className="pb-3 pr-4">Updated</th>
                    <th className="pb-3 pr-4">Skipped</th>
                    <th className="pb-3 pr-4">Failed</th>
                    <th className="pb-3">Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {(runs ?? []).map((run) => (
                    <tr key={run.run_id} data-testid={OPERATOR.historyRow(run.run_id)} className="border-t border-border">
                      <td className="py-3 pr-4 text-brand-teal/70">{when(run.finished_at)}</td>
                      <td className="py-3 pr-4">{run.program_slug ?? "All plans"}</td>
                      <td className="py-3 pr-4">{run.mode}</td>
                      <td className="py-3 pr-4">{run.recipes_updated}</td>
                      <td className="py-3 pr-4">{run.recipes_skipped ?? 0}</td>
                      <td className={`py-3 pr-4 ${run.recipes_failed ? "text-brand-orangeInk" : ""}`}>
                        {run.recipes_failed}
                      </td>
                      <td className="py-3">{run.pending_mapping ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {runs?.length === 0 && <p className="text-sm text-brand-teal/60">No syncs have run yet.</p>}
            </div>
          </section>
        )}
      </div>
    </AppShell>
  );
}
